import { supabase } from '../lib/supabase';

export interface UserStat {
    id: string;
    email: string | null;
    full_name: string | null;
    subscription_status: 'free' | 'pro' | 'inactive' | 'basic';
    country: string | null;
    user_since: string;
    total_leads: number;
    prospected_leads: number;
    total_searches: number;
    total_tokens_used: number;
    total_cost: number;
    last_activity: string | null;
    whatsapp_instance: string | null;
    whatsapp_connected: boolean | null;
}

export interface AdminStatsResponse {
    data: UserStat[] | null;
    error: any;
}

/**
 * Busca estatísticas de todos os usuários (apenas para admins)
 */
export const fetchAdminStats = async (): Promise<UserStat[]> => {
    const { data, error } = await supabase.functions.invoke<AdminStatsResponse>('admin-stats');

    if (error) {
        console.error('Error fetching admin stats:', error);
        throw error;
    }

    return data?.data || [];
};

/**
 * Atualiza o plano de assinatura de um usuário via RPC function
 * Usa uma função SQL com SECURITY DEFINER para bypass RLS
 */
export const updateUserSubscription = async (
    userId: string,
    newStatus: 'free' | 'pro' | 'inactive' | 'basic'
): Promise<{ success: boolean; error?: any }> => {
    try {
        const { data, error } = await supabase.rpc('update_user_subscription', {
            target_user_id: userId,
            new_status: newStatus
        });

        if (error) {
            console.error('Error updating subscription:', error);
            return { success: false, error };
        }

        // A função RPC retorna um JSONB com {success: true/false, error?: string}
        if (data && typeof data === 'object') {
            if (data.success) {
                return { success: true };
            } else {
                return { success: false, error: data.error || 'Unknown error' };
            }
        }

        return { success: true };
    } catch (error) {
        console.error('Error in updateUserSubscription:', error);
        return { success: false, error };
    }
};

/**
 * Atualiza o plano/status de vários usuários de uma vez (ativar, desativar,
 * trocar de plano em massa). Reaproveita a mesma RPC do update individual,
 * disparando uma chamada por usuário em paralelo.
 */
export const bulkUpdateUserSubscription = async (
    userIds: string[],
    newStatus: 'free' | 'pro' | 'inactive' | 'basic'
): Promise<{ successCount: number; failedIds: string[] }> => {
    const results = await Promise.allSettled(
        userIds.map((id) => updateUserSubscription(id, newStatus))
    );

    const failedIds: string[] = [];
    let successCount = 0;

    results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
            successCount++;
        } else {
            failedIds.push(userIds[index]);
        }
    });

    return { successCount, failedIds };
};

export interface UserUsageRecord {
    id: string;
    created_at: string;
    usage_type: string;
    model_name: string;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
}

export interface UserBulkSendRecord {
    id: string;
    created_at: string;
    total_recipients: number;
    messages_sent: number;
    messages_failed: number;
    status: string;
}

export interface UserSpendBreakdown {
    costLast30Days: number;
    recentUsage: UserUsageRecord[];
    recentBulkSends: UserBulkSendRecord[];
}

/**
 * Busca o detalhamento de custo/gasto de um usuário específico (para o modal
 * "ver mais informações" no painel de admin): gasto nos últimos 30 dias,
 * uso de IA recente e disparos em massa recentes.
 */
export const fetchUserSpendBreakdown = async (userId: string): Promise<UserSpendBreakdown> => {
    const since30Days = new Date();
    since30Days.setDate(since30Days.getDate() - 30);

    const [usageRes, bulkRes, recentCostRes] = await Promise.all([
        supabase
            .from('token_usage')
            .select('id, created_at, usage_type, model_name, input_tokens, output_tokens, cost_usd')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(10),
        supabase
            .from('bulk_message_history')
            .select('id, created_at, total_recipients, messages_sent, messages_failed, status')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(5),
        supabase
            .from('token_usage')
            .select('cost_usd')
            .eq('user_id', userId)
            .gte('created_at', since30Days.toISOString()),
    ]);

    const costLast30Days = (recentCostRes.data || []).reduce(
        (sum, r: any) => sum + (r.cost_usd || 0),
        0
    );

    return {
        costLast30Days,
        recentUsage: (usageRes.data as UserUsageRecord[]) || [],
        recentBulkSends: (bulkRes.data as UserBulkSendRecord[]) || [],
    };
};

/**
 * Busca detalhes completos de um usuário específico
 */
export const fetchUserDetails = async (userId: string) => {
    try {
        // Buscar perfil
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

        if (profileError) {
            return { data: null, error: profileError };
        }

        // Buscar credenciais (sem expor API keys)
        const { data: credentials } = await supabase
            .from('user_credentials')
            .select('integration_name, is_active, instance_details')
            .eq('user_id', userId);

        // Buscar contadores
        const { count: leadsCount } = await supabase
            .from('leads')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId);

        const { count: prospectedCount } = await supabase
            .from('leads')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('prospected', true);

        return {
            data: {
                profile,
                credentials: credentials || [],
                stats: {
                    total_leads: leadsCount || 0,
                    prospected_leads: prospectedCount || 0,
                },
            },
            error: null,
        };
    } catch (error) {
        console.error('Error in fetchUserDetails:', error);
        return { data: null, error };
    }
};

export const ADMIN_IMPERSONATION_KEY = 'admin_impersonation_backup';

export interface ImpersonationBackup {
    admin_access_token: string;
    admin_refresh_token: string;
    admin_id: string;
    admin_email?: string;
    admin_name?: string;
    target_user_id: string;
    target_user_email: string;
    target_user_name: string;
    started_at: string;
}

/**
 * Retorna as informações da sessão impersonada atual (se houver)
 */
export const getImpersonationInfo = (): {
    isImpersonating: boolean;
    targetUserName?: string;
    targetUserEmail?: string;
    targetUserId?: string;
} => {
    try {
        const raw = localStorage.getItem(ADMIN_IMPERSONATION_KEY);
        if (!raw) return { isImpersonating: false };
        const data: ImpersonationBackup = JSON.parse(raw);
        return {
            isImpersonating: true,
            targetUserName: data.target_user_name,
            targetUserEmail: data.target_user_email,
            targetUserId: data.target_user_id,
        };
    } catch {
        return { isImpersonating: false };
    }
};

/**
 * Inicia o acesso na conta do usuário alvo (Impersonation).
 * Salva a sessão do admin no localStorage e troca a sessão ativa do Supabase para o usuário.
 */
export const impersonateUser = async (
    targetUserId: string,
    targetName?: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        // 1. Obter a sessão e usuário admin atual
        const { data: { session: adminSession } } = await supabase.auth.getSession();
        const { data: { user: adminUser } } = await supabase.auth.getUser();

        if (!adminSession || !adminUser) {
            throw new Error('Sessão de administrador não encontrada.');
        }

        // 2. Chamar a Edge Function (tenta admin-stats com action impersonate, com fallback para admin-impersonate)
        let response = await supabase.functions.invoke('admin-stats', {
            body: { action: 'impersonate', targetUserId }
        });

        if (response.error || !response.data?.tokenHash) {
            response = await supabase.functions.invoke('admin-impersonate', {
                body: { targetUserId }
            });
        }

        if (response.error || !response.data?.tokenHash) {
            const errMsg = response.data?.error || response.error?.message || 'Falha ao gerar credencial de acesso para o usuário.';
            throw new Error(errMsg);
        }

        const { email: targetEmail, tokenHash } = response.data;

        // 3. Salvar o backup da sessão do Administrador no localStorage
        const backup: ImpersonationBackup = {
            admin_access_token: adminSession.access_token,
            admin_refresh_token: adminSession.refresh_token,
            admin_id: adminUser.id,
            admin_email: adminUser.email,
            admin_name: adminUser.user_metadata?.full_name,
            target_user_id: targetUserId,
            target_user_email: targetEmail,
            target_user_name: targetName || targetEmail,
            started_at: new Date().toISOString(),
        };
        localStorage.setItem(ADMIN_IMPERSONATION_KEY, JSON.stringify(backup));

        // 4. Autenticar no Supabase usando o token gerado para o usuário alvo
        let verifyRes = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'magiclink',
        });

        if (verifyRes.error) {
            verifyRes = await supabase.auth.verifyOtp({
                token_hash: tokenHash,
                type: 'email',
            });
        }

        if (verifyRes.error) {
            localStorage.removeItem(ADMIN_IMPERSONATION_KEY);
            throw new Error(verifyRes.error.message || 'Erro ao autenticar como usuário.');
        }

        return { success: true };
    } catch (err) {
        console.error('Erro em impersonateUser:', err);
        return {
            success: false,
            error: err instanceof Error ? err.message : String(err)
        };
    }
};

/**
 * Encerra o modo de impersonação e restaura a sessão original do Administrador.
 */
export const stopImpersonation = async (): Promise<{ success: boolean; error?: string }> => {
    try {
        const raw = localStorage.getItem(ADMIN_IMPERSONATION_KEY);
        if (!raw) {
            return { success: false, error: 'Nenhuma sessão de impersonação ativa.' };
        }

        const backup: ImpersonationBackup = JSON.parse(raw);
        localStorage.removeItem(ADMIN_IMPERSONATION_KEY);

        // Restaurar sessão do administrador via setSession
        if (backup.admin_access_token && backup.admin_refresh_token) {
            const { error } = await supabase.auth.setSession({
                access_token: backup.admin_access_token,
                refresh_token: backup.admin_refresh_token,
            });

            if (error) {
                console.error('Erro ao restaurar sessão admin:', error);
                window.location.href = '/admin';
                return { success: false, error: error.message };
            }
        }

        return { success: true };
    } catch (err) {
        console.error('Erro em stopImpersonation:', err);
        localStorage.removeItem(ADMIN_IMPERSONATION_KEY);
        return {
            success: false,
            error: err instanceof Error ? err.message : String(err)
        };
    }
};

